import React, { useState, useMemo } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../db';
import FloorPlanEditor from './FloorPlanEditor';
import { usableG } from '../utils/priceYield';   // V152 실사용량 (신선식품 로스 제외)

const STATUS_MAP = {
  estimate:     { label: '견적', color: '#EA580C', bg: '#F4F4F5' },
  construction: { label: '공사중', color: '#2563eb', bg: '#eff6ff' },
  complete:     { label: '완료', color: '#18181B', bg: '#F4F4F5' },
};

const DEFAULT_STAGES = [
  { id: 's1', name: '철거', progress: 0 },
  { id: 's2', name: '전기/설비', progress: 0 },
  { id: 's3', name: '바닥재', progress: 0 },
  { id: 's4', name: '도배/벽지', progress: 0 },
  { id: 's5', name: '페인트', progress: 0 },
  { id: 's6', name: '마감/청소', progress: 0 },
];

function newId() { return 'proj_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7); }

function calcMaterialCost(items, area, prices) {
  if (!items || !prices || area <= 0) return 0;
  return items.reduce((sum, item) => {
    const cost = (item.ingredients || []).reduce((s, ing) => {
      const p = prices.find(p => p.name === ing.name);
      if (!p || !usableG(p)) return s;
      return s + (ing.qty / usableG(p)) * p.price;   // V152
    }, 0);
    const ratio = item.serving_g > 0 ? area / item.serving_g : 1;
    return sum + cost * ratio;
  }, 0);
}

function generateEstimate(baseMaterial, laborPerM2, area, markup) {
  const labor = laborPerM2 * area;
  const base = baseMaterial + labor;
  return {
    low:  Math.round(base * (1 + markup.low  / 100) / 1000) * 1000,
    mid:  Math.round(base * (1 + markup.mid  / 100) / 1000) * 1000,
    high: Math.round(base * (1 + markup.high / 100) / 1000) * 1000,
  };
}

function calcOverallProgress(stages) {
  if (!stages || stages.length === 0) return 0;
  const sum = stages.reduce((s, st) => s + (st.progress || 0), 0);
  return Math.round(sum / stages.length);
}

const DEFAULT_MARKUP = { low: 15, mid: 30, high: 50 };
const fmt = (n) => n ? n.toLocaleString() + '원' : '-';

function ProgressBar({ value, height = 6, color = '#FF6F0F', bg = '#e5e7eb', showLabel = false }) {
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ height, borderRadius: height, background: bg, overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          width: value + '%',
          background: value >= 100 ? '#FF6F0F' : value >= 60 ? color : value >= 30 ? '#A1A1AA' : '#e5e7eb',
          borderRadius: height,
          transition: 'width 0.4s ease',
        }} />
      </div>
      {showLabel && (
        <span style={{ position: 'absolute', right: 0, top: '-18px', fontSize: '11px', fontWeight: '700', color: '#374151' }}>
          {value}%
        </span>
      )}
    </div>
  );
}

export default function ProjectManager({ user, categories, prices, effectiveMode, terms, saveProject, deleteProject, projects }) {
  const [view, setView]             = useState('list');
  const [activeProj, setActiveProj] = useState(null);
  const [detailTab, setDetailTab]   = useState('progress');

  const [form, setForm] = useState({ customerName: '', address: '', phone: '', area: '', note: '' });

  const [estArea, setEstArea]             = useState('');
  const [laborPerM2, setLaborPerM2]       = useState(30000);
  const [markup, setMarkup]               = useState(DEFAULT_MARKUP);
  const [selectedItems, setSelectedItems] = useState([]);
  const [estSaved, setEstSaved]           = useState(false);

  const [logText, setLogText]   = useState('');
  const [infoForm, setInfoForm] = useState(null);

  // 공정 단계 편집 상태
  const [newStageName, setNewStageName] = useState('');
  const [shareUrl, setShareUrl]       = useState('');
  const [shareCopied, setShareCopied] = useState(false);

  const allItems = useMemo(() =>
    (categories || []).flatMap(c => c.items), [categories]);

  const openProject = (proj) => {
    // 기존 프로젝트에 stages 없으면 기본값 부여
    const withStages = proj.stages ? proj : { ...proj, stages: DEFAULT_STAGES.map(s => ({ ...s })) };
    setActiveProj(withStages);
    setDetailTab('progress');
    setEstArea(withStages.area || '');
    setLaborPerM2(withStages.laborPerM2 || 30000);
    setMarkup(withStages.markup || DEFAULT_MARKUP);
    setSelectedItems(withStages.estimateItems || []);
    setEstSaved(false);
    setLogText('');
    setInfoForm({ ...withStages });
    setView('detail');
  };

  const handleCreate = () => {
    if (!form.customerName.trim()) return;
    const proj = {
      id: newId(),
      customerName: form.customerName.trim(),
      address: form.address.trim(),
      phone: form.phone.trim(),
      area: parseFloat(form.area) || 0,
      note: form.note.trim(),
      status: 'estimate',
      createdAt: new Date().toISOString(),
      estimateItems: [],
      estimate: null,
      logs: [],
      stages: DEFAULT_STAGES.map(s => ({ ...s })),
    };
    saveProject(proj);
    setForm({ customerName: '', address: '', phone: '', area: '', note: '' });
    openProject(proj);
  };

  const handleSaveEstimate = () => {
    const area = parseFloat(estArea) || 0;
    const matCost = calcMaterialCost(
      selectedItems.map(id => allItems.find(i => i.id === id)).filter(Boolean),
      area, prices
    );
    const est = generateEstimate(matCost, laborPerM2, area, markup);
    const updated = { ...activeProj, area, laborPerM2, markup, estimateItems: selectedItems, estimate: est };
    saveProject(updated);
    setActiveProj(updated);
    setEstSaved(true);
  };

  const handleAddLog = () => {
    if (!logText.trim()) return;
    const entry = {
      id: newId(),
      text: logText.trim(),
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }),
    };
    const updated = { ...activeProj, logs: [...(activeProj.logs || []), entry] };
    saveProject(updated);
    setActiveProj(updated);
    setLogText('');
  };

  const handleDeleteLog = (logId) => {
    const updated = { ...activeProj, logs: (activeProj.logs || []).filter(l => l.id !== logId) };
    saveProject(updated);
    setActiveProj(updated);
  };

  const handleSaveInfo = () => {
    const updated = { ...activeProj, ...infoForm };
    saveProject(updated);
    setActiveProj(updated);
  };

  const handleStatusChange = (status) => {
    const updated = { ...activeProj, status };
    saveProject(updated);
    setActiveProj(updated);
  };

  const handleDeleteProject = () => {
    if (!window.confirm('프로젝트를 삭제하시겠습니까?')) return;
    deleteProject(activeProj.id);
    setView('list');
  };

  // ── 공정 진행률 업데이트
  const handleStageProgress = (stageId, value) => {
    const stages = (activeProj.stages || []).map(s =>
      s.id === stageId ? { ...s, progress: value } : s
    );
    const updated = { ...activeProj, stages };
    setActiveProj(updated);
    saveProject(updated);
  };

  // ── 공유 링크 생성
  const handleCreateShare = async () => {
    const shareId = 'sh_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
    const shareData = {
      customerName: proj.customerName,
      address: proj.address || '',
      area: proj.area || 0,
      note: proj.note || '',
      status: proj.status,
      estimate: proj.estimate || null,
      stages: (proj.stages || []).map(s => ({ id: s.id, name: s.name, progress: s.progress })),
      sharedAt: new Date().toISOString(),
    };
    try {
      await setDoc(doc(db, 'shares', shareId), shareData);
      const url = window.location.origin + '/share/' + shareId;
      setShareUrl(url);
      setShareCopied(false);
      navigator.clipboard.writeText(url).catch(() => {});
    } catch (e) {
      alert('공유 링크 생성 실패: Firestore 보안 규칙을 확인해주세요.');
      console.error(e);
    }
  };

  // ── 공정 단계 추가
  const handleAddStage = () => {
    if (!newStageName.trim()) return;
    const stage = { id: 'stage_' + Date.now(), name: newStageName.trim(), progress: 0 };
    const stages = [...(activeProj.stages || []), stage];
    const updated = { ...activeProj, stages };
    setActiveProj(updated);
    saveProject(updated);
    setNewStageName('');
  };

  // ── 공정 단계 삭제
  const handleDeleteStage = (stageId) => {
    const stages = (activeProj.stages || []).filter(s => s.id !== stageId);
    const updated = { ...activeProj, stages };
    setActiveProj(updated);
    saveProject(updated);
  };

  // ─── 목록 뷰
  if (view === 'list') {
    const sorted = [...(projects || [])].sort((a, b) =>
      new Date(b.createdAt) - new Date(a.createdAt));

    return (
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px', background: '#f0f2f5' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: '#111827' }}>
            🏗️ 프로젝트 관리
          </h2>
          <button onClick={() => setView('create')}
            style={{
              padding: '10px 20px', borderRadius: '10px', border: 'none',
              background: '#FF6F0F', color: '#fff', fontWeight: '700',
              fontSize: '14px', cursor: 'pointer',
            }}>
            + 새 프로젝트
          </button>
        </div>

        {sorted.length === 0 && (
          <div style={{ textAlign: 'center', padding: '80px 20px', color: '#9ca3af' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏚️</div>
            <p style={{ fontSize: '15px', marginBottom: '8px' }}>아직 등록된 프로젝트가 없어요</p>
            <p style={{ fontSize: '13px' }}>+ 새 프로젝트를 눌러 첫 프로젝트를 만들어보세요</p>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '14px' }}>
          {sorted.map(proj => {
            const st = STATUS_MAP[proj.status] || STATUS_MAP.estimate;
            const overall = calcOverallProgress(proj.stages);
            return (
              <div key={proj.id} onClick={() => openProject(proj)}
                style={{
                  background: '#fff', borderRadius: '14px', padding: '18px 20px',
                  cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                  border: '1px solid #e5e7eb',
                  transition: 'box-shadow 0.15s, transform 0.1s',
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.07)'; e.currentTarget.style.transform = ''; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                  <span style={{ fontWeight: '700', fontSize: '15px', color: '#111827' }}>
                    {proj.customerName}
                  </span>
                  <span style={{
                    fontSize: '12px', fontWeight: '700',
                    color: st.color, background: st.bg,
                    padding: '3px 10px', borderRadius: '20px',
                  }}>{st.label}</span>
                </div>
                {proj.address && (
                  <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#6b7280' }}>
                    📍 {proj.address}
                  </p>
                )}
                {proj.area > 0 && (
                  <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#6b7280' }}>
                    📐 {proj.area}㎡
                  </p>
                )}
                {/* 전체 공정 진행률 */}
                {proj.stages && proj.stages.length > 0 && (
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontSize: '11px', color: '#9ca3af' }}>공정 진행률</span>
                      <span style={{ fontSize: '11px', fontWeight: '700', color: overall >= 100 ? '#FF6F0F' : '#374151' }}>
                        {overall}%
                      </span>
                    </div>
                    <ProgressBar value={overall} height={6} />
                  </div>
                )}
                {proj.estimate && (
                  <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#18181B', fontWeight: '600' }}>
                    💰 {fmt(proj.estimate.mid)} (중간 견적)
                  </p>
                )}
                <p style={{ margin: '8px 0 0', fontSize: '11px', color: '#d1d5db' }}>
                  {proj.createdAt ? new Date(proj.createdAt).toLocaleDateString('ko-KR') : ''}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ─── 생성 폼
  if (view === 'create') {
    return (
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px', background: '#f0f2f5' }}>
        <div style={{ maxWidth: '520px', margin: '0 auto' }}>
          <button onClick={() => setView('list')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '14px', marginBottom: '16px', padding: 0 }}>
            ← 목록으로
          </button>
          <h2 style={{ margin: '0 0 20px', fontSize: '20px', fontWeight: '800' }}>새 프로젝트</h2>
          <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            {[
              { key: 'customerName', label: '고객명 *', placeholder: '홍길동', type: 'text' },
              { key: 'address',      label: '시공 주소',  placeholder: '서울시 강남구 ...', type: 'text' },
              { key: 'phone',        label: '연락처',    placeholder: '010-0000-0000', type: 'text' },
              { key: 'area',         label: '시공 면적 (㎡)', placeholder: '예: 85', type: 'number' },
            ].map(({ key, label, placeholder, type }) => (
              <div key={key} style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  {label}
                </label>
                <input type={type} value={form[key]}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  placeholder={placeholder}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #e5e7eb', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                  onFocus={e => e.target.style.borderColor = '#FF6F0F'}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
            ))}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>메모</label>
              <textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                placeholder="특이사항, 요청사항 등" rows={3}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #e5e7eb', fontSize: '14px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                onFocus={e => e.target.style.borderColor = '#FF6F0F'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            <button onClick={handleCreate} disabled={!form.customerName.trim()}
              style={{
                width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
                background: form.customerName.trim() ? '#FF6F0F' : '#d1d5db',
                color: '#fff', fontWeight: '700', fontSize: '15px',
                cursor: form.customerName.trim() ? 'pointer' : 'not-allowed',
              }}>
              프로젝트 생성
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── 상세 뷰
  const proj = activeProj;
  const st = STATUS_MAP[proj.status] || STATUS_MAP.estimate;
  const overall = calcOverallProgress(proj.stages);

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: '#f0f2f5' }}>
      {/* 상세 헤더 */}
      <div style={{
        background: '#fff', borderBottom: '1px solid #e5e7eb',
        padding: '16px 24px', position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <button onClick={() => setView('list')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '14px', padding: 0 }}>
            ← 목록
          </button>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '800', flex: 1 }}>
            {proj.customerName}
          </h2>
          {proj.address && <span style={{ fontSize: '13px', color: '#6b7280' }}>📍 {proj.address}</span>}
          <select value={proj.status} onChange={e => handleStatusChange(e.target.value)}
            style={{
              padding: '5px 10px', borderRadius: '20px', border: 'none',
              background: st.bg, color: st.color, fontWeight: '700', fontSize: '13px', cursor: 'pointer',
            }}>
            <option value="estimate">견적</option>
            <option value="construction">공사중</option>
            <option value="complete">완료</option>
          </select>
        </div>

        {/* 전체 진행률 바 (항상 표시) */}
        {proj.stages && proj.stages.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
              <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>전체 공정 진행률</span>
              <span style={{ fontSize: '12px', fontWeight: '800', color: overall >= 100 ? '#FF6F0F' : '#FF6F0F' }}>
                {overall}%
              </span>
            </div>
            <ProgressBar value={overall} height={8} />
          </div>
        )}

        {/* 탭 */}
        <div style={{ display: 'flex', gap: '4px', marginTop: '12px' }}>
          {[
            { id: 'progress', label: '📊 공정' },
            { id: 'estimate', label: '📋 견적' },
            { id: 'floorplan', label: '📐 평면도' },
            { id: 'log',      label: '🪵 공사일지' },
            { id: 'info',     label: '⚙️ 정보' },
          ].map(t => (
            <button key={t.id} onClick={() => setDetailTab(t.id)}
              style={{
                padding: '7px 14px', borderRadius: '8px', border: 'none',
                background: detailTab === t.id ? '#FF6F0F' : '#f3f4f6',
                color: detailTab === t.id ? '#fff' : '#6b7280',
                fontWeight: '700', fontSize: '13px', cursor: 'pointer',
              }}>{t.label}</button>
          ))}
        </div>
      </div>

      <div style={{ padding: detailTab === 'floorplan' ? 0 : '24px', maxWidth: detailTab === 'floorplan' ? 'none' : '720px' }}>

        {/* ── 공정 진행률 탭 ── */}
        {detailTab === 'progress' && (
          <div>
            {/* 단계별 카드 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              {(proj.stages || []).map((stage, idx) => (
                <div key={stage.id} style={{
                  background: '#fff', borderRadius: '12px', padding: '16px 18px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                  borderLeft: stage.progress >= 100 ? '4px solid #FF6F0F' : stage.progress > 0 ? '4px solid #FF6F0F' : '4px solid #e5e7eb',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        width: '22px', height: '22px', borderRadius: '50%',
                        background: stage.progress >= 100 ? '#FF6F0F' : '#e5e7eb',
                        color: stage.progress >= 100 ? '#fff' : '#9ca3af',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '11px', fontWeight: '800', flexShrink: 0,
                      }}>
                        {stage.progress >= 100 ? '✓' : idx + 1}
                      </span>
                      <span style={{
                        fontWeight: '700', fontSize: '14px',
                        color: stage.progress >= 100 ? '#FF6F0F' : '#111827',
                        textDecoration: stage.progress >= 100 ? 'line-through' : 'none',
                        textDecorationColor: '#FF6F0F',
                      }}>
                        {stage.name}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '800', color: stage.progress >= 100 ? '#FF6F0F' : '#374151', minWidth: '36px', textAlign: 'right' }}>
                        {stage.progress}%
                      </span>
                      <button onClick={() => handleDeleteStage(stage.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: '16px', padding: 0, lineHeight: 1 }}>
                        ×
                      </button>
                    </div>
                  </div>
                  {/* 슬라이더 */}
                  <input type="range" min={0} max={100} step={5}
                    value={stage.progress}
                    onChange={e => handleStageProgress(stage.id, Number(e.target.value))}
                    style={{ width: '100%', accentColor: '#FF6F0F', cursor: 'pointer' }}
                  />
                  {/* 빠른 선택 버튼 */}
                  <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                    {[0, 25, 50, 75, 100].map(v => (
                      <button key={v} onClick={() => handleStageProgress(stage.id, v)}
                        style={{
                          padding: '3px 10px', borderRadius: '6px', border: 'none',
                          background: stage.progress === v ? '#FF6F0F' : '#f3f4f6',
                          color: stage.progress === v ? '#fff' : '#6b7280',
                          fontSize: '11px', fontWeight: '700', cursor: 'pointer',
                        }}>
                        {v === 100 ? '완료' : v + '%'}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* 공정 단계 추가 */}
            <div style={{ background: '#fff', borderRadius: '12px', padding: '16px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: '700', color: '#374151' }}>공정 단계 추가</p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  value={newStageName}
                  onChange={e => setNewStageName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAddStage(); }}
                  placeholder="예: 타일, 목공, 창호 ..."
                  style={{ flex: 1, padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e5e7eb', fontSize: '14px', outline: 'none' }}
                  onFocus={e => e.target.style.borderColor = '#FF6F0F'}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                />
                <button onClick={handleAddStage} disabled={!newStageName.trim()}
                  style={{
                    padding: '9px 16px', borderRadius: '8px', border: 'none',
                    background: newStageName.trim() ? '#FF6F0F' : '#d1d5db',
                    color: '#fff', fontWeight: '700', fontSize: '13px',
                    cursor: newStageName.trim() ? 'pointer' : 'not-allowed',
                  }}>
                  추가
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── 견적 탭 ── */}
        {detailTab === 'estimate' && (
          <div>
            <div style={{ background: '#fff', borderRadius: '14px', padding: '20px', marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: '700' }}>기본 설정</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>시공 면적 (㎡)</label>
                  <input type="number" value={estArea} onChange={e => setEstArea(e.target.value)} placeholder="85"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e5e7eb', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                    onFocus={e => e.target.style.borderColor = '#FF6F0F'}
                    onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>인건비 (원/㎡)</label>
                  <input type="number" value={laborPerM2} onChange={e => setLaborPerM2(Number(e.target.value))}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e5e7eb', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                    onFocus={e => e.target.style.borderColor = '#FF6F0F'}
                    onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                {[{ key: 'low', label: '저가 마진 (%)' }, { key: 'mid', label: '중간 마진 (%)' }, { key: 'high', label: '프리미엄 마진 (%)' }].map(({ key, label }) => (
                  <div key={key}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>{label}</label>
                    <input type="number" value={markup[key]} onChange={e => setMarkup(m => ({ ...m, [key]: Number(e.target.value) }))}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e5e7eb', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                      onFocus={e => e.target.style.borderColor = '#FF6F0F'}
                      onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                ))}
              </div>
            </div>
            {allItems.length > 0 && (
              <div style={{ background: '#fff', borderRadius: '14px', padding: '20px', marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: '700' }}>시공 항목 선택</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {allItems.map(item => {
                    const on = selectedItems.includes(item.id);
                    return (
                      <button key={item.id}
                        onClick={() => setSelectedItems(prev => on ? prev.filter(id => id !== item.id) : [...prev, item.id])}
                        style={{
                          padding: '6px 14px', borderRadius: '20px', fontSize: '13px',
                          border: on ? 'none' : '1.5px solid #e5e7eb',
                          background: on ? '#FF6F0F' : '#f9fafb',
                          color: on ? '#fff' : '#374151',
                          fontWeight: on ? '700' : '500', cursor: 'pointer',
                        }}>{item.name}</button>
                    );
                  })}
                </div>
              </div>
            )}
            {(() => {
              const area = parseFloat(estArea) || 0;
              const matCost = calcMaterialCost(
                selectedItems.map(id => allItems.find(i => i.id === id)).filter(Boolean), area, prices || []);
              const est = area > 0 ? generateEstimate(matCost, laborPerM2, area, markup) : null;
              return est ? (
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: '700' }}>견적 3안</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    {[
                      { key: 'low', label: '저가', emoji: '💛', color: '#EA580C', bg: '#FAFAFA' },
                      { key: 'mid', label: '중간', emoji: '💚', color: '#18181B', bg: '#FAFAFA' },
                      { key: 'high', label: '프리미엄', emoji: '💎', color: '#7c3aed', bg: '#f5f3ff' },
                    ].map(({ key, label, emoji, color, bg }) => (
                      <div key={key} style={{ background: bg, borderRadius: '12px', padding: '16px', textAlign: 'center', border: '1px solid ' + color + '33' }}>
                        <div style={{ fontSize: '22px', marginBottom: '6px' }}>{emoji}</div>
                        <div style={{ fontSize: '12px', fontWeight: '700', color, marginBottom: '8px' }}>{label}</div>
                        <div style={{ fontSize: '18px', fontWeight: '800', color: '#111827' }}>
                          {(est[key] / 10000).toLocaleString()}만원
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>마진 {markup[key]}%</div>
                      </div>
                    ))}
                  </div>
                  {matCost > 0 && (
                    <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px', textAlign: 'right' }}>
                      자재비 {fmt(Math.round(matCost))} · 인건비 {fmt(laborPerM2 * area)} · 합계(원가) {fmt(Math.round(matCost + laborPerM2 * area))}
                    </p>
                  )}
                </div>
              ) : null;
            })()}
            <button onClick={handleSaveEstimate}
              style={{ width: '100%', padding: '13px', borderRadius: '10px', border: 'none', background: '#FF6F0F', color: '#fff', fontWeight: '700', fontSize: '15px', cursor: 'pointer' }}>
              {estSaved ? '✓ 견적 저장됨' : '견적 저장'}
            </button>

            {/* 공유 링크 */}
            {proj.estimate && (
              <div style={{ marginTop: '12px' }}>
                <button onClick={handleCreateShare}
                  style={{ width: '100%', padding: '11px', borderRadius: '10px', border: '1.5px solid #FF6F0F', background: '#fff', color: '#18181B', fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
                  🔗 고객 공유 링크 생성
                </button>
                {shareUrl && (
                  <div style={{ marginTop: '10px', background: '#FAFAFA', borderRadius: '10px', padding: '12px 14px', border: '1px solid #F4F4F5' }}>
                    <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#18181B', fontWeight: '700' }}>✓ 링크가 클립보드에 복사되었습니다</p>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <input readOnly value={shareUrl}
                        style={{ flex: 1, padding: '7px 10px', borderRadius: '7px', border: '1px solid #F4F4F5', fontSize: '12px', color: '#374151', background: '#fff', outline: 'none' }}
                        onClick={e => e.target.select()}
                      />
                      <button
                        onClick={() => { navigator.clipboard.writeText(shareUrl); setShareCopied(true); setTimeout(() => setShareCopied(false), 2000); }}
                        style={{ padding: '7px 12px', borderRadius: '7px', border: 'none', background: '#FF6F0F', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}>
                        {shareCopied ? '✓' : '복사'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── 평면도 탭 ── */}
        {detailTab === 'floorplan' && (
          <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden', height: 'calc(100vh - 200px)', minHeight: 600 }}>
            <FloorPlanEditor
              project={proj}
              onSave={(updatedProj) => saveProject(updatedProj)}
            />
          </div>
        )}

        {/* ── 공사일지 탭 ── */}
        {detailTab === 'log' && (
          <div>
            <div style={{ background: '#fff', borderRadius: '14px', padding: '20px', marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: '700' }}>공사일지 작성</h3>
              <textarea value={logText} onChange={e => setLogText(e.target.value)}
                placeholder="오늘의 공사 내용을 기록하세요..." rows={3}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #e5e7eb', fontSize: '14px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                onFocus={e => e.target.style.borderColor = '#FF6F0F'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />
              <button onClick={handleAddLog} disabled={!logText.trim()}
                style={{
                  marginTop: '10px', padding: '9px 20px', borderRadius: '8px', border: 'none',
                  background: logText.trim() ? '#FF6F0F' : '#d1d5db',
                  color: '#fff', fontWeight: '700', fontSize: '13px',
                  cursor: logText.trim() ? 'pointer' : 'not-allowed',
                }}>
                기록 추가
              </button>
            </div>
            {(proj.logs || []).length === 0 && (
              <p style={{ color: '#9ca3af', textAlign: 'center', padding: '40px 0', fontSize: '14px' }}>
                아직 공사일지가 없어요
              </p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[...(proj.logs || [])].reverse().map(log => (
                <div key={log.id} style={{
                  background: '#fff', borderRadius: '12px', padding: '14px 16px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderLeft: '4px solid #FF6F0F',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>{log.date} {log.time}</div>
                    <div style={{ fontSize: '14px', color: '#111827', whiteSpace: 'pre-wrap' }}>{log.text}</div>
                  </div>
                  <button onClick={() => handleDeleteLog(log.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: '16px', padding: '0 0 0 10px' }}>×</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── 정보 탭 ── */}
        {detailTab === 'info' && infoForm && (
          <div>
            <div style={{ background: '#fff', borderRadius: '14px', padding: '20px', marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: '700' }}>고객 정보 수정</h3>
              {[
                { key: 'customerName', label: '고객명', type: 'text' },
                { key: 'address',      label: '시공 주소', type: 'text' },
                { key: 'phone',        label: '연락처', type: 'text' },
                { key: 'area',         label: '시공 면적 (㎡)', type: 'number' },
              ].map(({ key, label, type }) => (
                <div key={key} style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>{label}</label>
                  <input type={type} value={infoForm[key] || ''} onChange={e => setInfoForm(f => ({ ...f, [key]: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e5e7eb', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                    onFocus={e => e.target.style.borderColor = '#FF6F0F'}
                    onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              ))}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>메모</label>
                <textarea value={infoForm.note || ''} onChange={e => setInfoForm(f => ({ ...f, note: e.target.value }))} rows={3}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e5e7eb', fontSize: '14px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                  onFocus={e => e.target.style.borderColor = '#FF6F0F'}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
              <button onClick={handleSaveInfo}
                style={{ width: '100%', padding: '11px', borderRadius: '10px', border: 'none', background: '#FF6F0F', color: '#fff', fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
                저장
              </button>
            </div>
            <div style={{ background: '#fff', borderRadius: '14px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: '700', color: '#52525B' }}>위험 구역</h3>
              <button onClick={handleDeleteProject}
                style={{ padding: '10px 20px', borderRadius: '8px', border: '1.5px solid #52525B', background: '#fff', color: '#52525B', fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
                🗑 프로젝트 삭제
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
